<template>
    <div class="PromptInput" :class="{ 'PromptInput--multiline': isMultiLine }">
        <div class="PromptInput__textareaWrapper">
            <textarea
                ref="textarea"
                class="PromptInput__textarea"
                rows="1"
                :value="value"
                :placeholder="animatePlaceholder ? '' : placeholder"
                @input="onInput"
                @keydown.enter.exact.prevent="onEnter"
            />
            <span
                v-if="animatePlaceholder && !value"
                class="PromptInput__animatedPlaceholder"
                aria-hidden="true"
            >{{ displayedPlaceholder }}</span>
        </div>
        <div class="PromptInput__actions">
            <NewButton
                v-if="canCancel"
                variant="fill"
                color="primary"
                size="icon-sm"
                type="button"
                aria-label="Cancel"
                @mousedown.prevent
                @click="$emit('cancel')"
            >
                <EidIcon :icon-src="faStop" />
            </NewButton>
            <NewButton
                v-else
                variant="gradient"
                color="primary"
                size="icon-sm"
                type="button"
                aria-label="Send message"
                :disabled="isLoading || !value.trim()"
                @mousedown.prevent
                @click="onSend"
            >
                <EidIcon :icon-src="faPaperPlane" />
            </NewButton>
        </div>
    </div>
</template>

<style lang="scss">
@keyframes PromptInput-blink {
    0%, 100% {
        opacity: 1;
    }

    50% {
        opacity: 0;
    }
}

.PromptInput {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--eidSecureBlue-25);
    border-radius: 12px;
    padding: 8px 12px;
    background: var(--eidWhite);

    &:focus-within {
        border-color: var(--eidSecureBlue-75);
    }

    &__textareaWrapper {
        position: relative;
        flex: 1;
        min-width: 0;
    }

    &__textarea {
        display: block;
        width: 100%;
        resize: none;
        overflow: hidden;
        border: none;
        background: none;
        outline: none;
        padding: 0;
        font-family: inherit;
        font-size: 16px;
        line-height: 1.5;
        color: var(--eidSecureBlue);
    }

    &__animatedPlaceholder {
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;
        user-select: none;
        color: var(--eidTechGray-50);
        font-size: 16px;
        line-height: 1.5;
        white-space: nowrap;
        overflow: hidden;
        max-width: 100%;

        &::after {
            content: "|";
            margin-left: 1px;
            animation: PromptInput-blink 1s step-end infinite;
        }
    }

    &__actions {
        flex-shrink: 0;
        display: flex;
        align-items: center;
    }

    &--multiline {
        flex-wrap: wrap;

        .PromptInput__textareaWrapper {
            width: 100%;
        }

        .PromptInput__actions {
            width: 100%;
            justify-content: flex-end;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
    import { faStop } from '@fortawesome/pro-solid-svg-icons'; // intentional solid weight for visual emphasis on the stop action

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';

    import NewButton from '@/components/new-button/NewButton.vue';

    const ROTATING_PROMPTS = [
        'Ask Evie about compliance, vendors, or reporting...',
        'What should I focus on this week?',
        'What\'s the riskiest thing in my portfolio right now?',
        'What is our overall compliance across active profiles?',
        'Which suppliers have already lapsed coverage?',
        'Which suppliers have been contacted multiple times without response?',
        'Are any suppliers refusing to provide updated documents?',
    ];

    const TYPE_IN_SPEED = 40;
    const TYPE_OUT_SPEED = 25;
    const PAUSE_AFTER_TYPED = 1800;
    const PAUSE_BETWEEN_PROMPTS = 300;

    export default defineComponent({
        name: 'PromptInput',
        components: { NewButton, EidIcon },
        props: {
            value: { type: String as PropType<string>, required: true },
            isLoading: { type: Boolean as PropType<boolean>, default: false },
            canCancel: { type: Boolean as PropType<boolean>, default: false },
            placeholder: { type: String as PropType<string>, default: 'Ask Evie anything...' },
            animatePlaceholder: { type: Boolean as PropType<boolean>, default: false },
        },
        emits: [ 'cancel', 'input', 'send' ],
        data() {
            return {
                faPaperPlane,
                faStop,
                rowCount: 1 as number,
                displayedPlaceholder: '' as string,
                promptIndex: 0 as number,
                placeholderTimer: null as ReturnType<typeof setTimeout> | null,
            };
        },
        computed: {
            isMultiLine(): boolean {
                return this.rowCount >= 2;
            },
        },
        watch: {
            value(newVal: string) {
                if (this.animatePlaceholder) {
                    if (newVal) {
                        this.stopAnimation();
                    } else {
                        this.startAnimation();
                    }
                }
                this.$nextTick(this.resize);
            },
        },
        mounted() {
            this.resize();
            if (this.animatePlaceholder) {
                this.startAnimation();
            }
        },
        beforeUnmount() {
            this.stopAnimation();
        },
        methods: {
            resize() {
                const textarea = this.$refs.textarea as HTMLTextAreaElement;
                if (!textarea) {
                    return;
                }
                textarea.style.height = '0px';
                const style = getComputedStyle(textarea);
                const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
                const maxHeight = lineHeight * 8;
                const newHeight = Math.min(textarea.scrollHeight, maxHeight);
                textarea.style.height = `${newHeight}px`;
                textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
                this.rowCount = Math.max(1, Math.ceil(newHeight / lineHeight));
            },
            startAnimation() {
                this.stopAnimation();
                this.typeIn();
            },
            stopAnimation() {
                if (this.placeholderTimer !== null) {
                    clearTimeout(this.placeholderTimer);
                    this.placeholderTimer = null;
                }
            },
            typeIn() {
                const target = ROTATING_PROMPTS[this.promptIndex];
                if (this.displayedPlaceholder.length < target.length) {
                    this.displayedPlaceholder = target.slice(0, this.displayedPlaceholder.length + 1);
                    this.placeholderTimer = setTimeout(this.typeIn, TYPE_IN_SPEED);
                } else {
                    this.placeholderTimer = setTimeout(this.typeOut, PAUSE_AFTER_TYPED);
                }
            },
            typeOut() {
                if (this.displayedPlaceholder.length > 0) {
                    this.displayedPlaceholder = this.displayedPlaceholder.slice(0, -1);
                    this.placeholderTimer = setTimeout(this.typeOut, TYPE_OUT_SPEED);
                } else {
                    this.promptIndex = (this.promptIndex + 1) % ROTATING_PROMPTS.length;
                    this.placeholderTimer = setTimeout(this.typeIn, PAUSE_BETWEEN_PROMPTS);
                }
            },
            onInput(event: Event) {
                const text = (event.target as HTMLTextAreaElement).value;
                this.$emit('input', text);
                this.resize();
            },
            onEnter() {
                this.onSend();
            },
            focus() {
                (this.$refs.textarea as HTMLTextAreaElement)?.focus();
            },
            onSend() {
                if (!this.value.trim() || this.isLoading) {
                    return;
                }
                this.$emit('send', this.value.trim());
                this.$emit('input', '');
            },
        },
    });
</script>
